// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// cues.ts — every sound this game can make, declared once.
//
// `sfx.play()` on a name no patch defines RETURNS SILENTLY. That is a shipped-and-green bug waiting
// to happen: a typo'd cue is invisible to every test, because nothing throws and nothing logs. So
// CUES is the declaration, tests/source-hygiene.test.ts greps every `sfx.play('...')` literal in
// src/ and fails on any name not in this list, and it also checks the reverse — that every declared
// name is one a real mixer knows.
//
// THE MIX IS QUIET ON PURPOSE. A factory game that chirps on every item movement is unbearable
// inside a minute, and the two sounds that matter — money arriving and ground going bad — would be
// buried under it. Nothing routine makes a sound at all. Placement, refusal, shipping and the rut
// crossing a tier are the only events with a voice.

import type { Patch } from '@ben-gy/game-engine/sound';

export const CUES = [
  'lay',
  'place',
  'pull',
  'deny',
  'ship',
  'sour',
  'scour',
  'select',
  'start',
  'whistle',
] as const;

export type Cue = (typeof CUES)[number];

export const PATCHES: Record<string, Patch> = {
  /** One belt cell going down under the finger. Deliberately tiny — a long run plays many. */
  lay: { type: 'square', freq: [300, 360], dur: 0.025, gain: 0.1 },
  /** A machine set down: heavier, with a bit of body. */
  place: { type: 'triangle', freq: [200, 320], dur: 0.11, gain: 0.24 },
  /** Something taken back up. The inverse glide, so the pair reads as opposites. */
  pull: { type: 'triangle', freq: [320, 180], dur: 0.1, gain: 0.2 },
  /** A refusal. Low, flat and unmusical, because it must never be mistaken for a success. */
  deny: { type: 'sine', freq: [90, 70], dur: 0.13, gain: 0.3 },
  /** An armature reaching the wharf. The only genuinely pleasant sound in the game. */
  ship: { type: 'square', freq: [740, 1180], dur: 0.13, gain: 0.2 },
  /** A cell crossing into a worse rut tier — the sound of ground you are spending. */
  sour: { type: 'sine', freq: [210, 150], dur: 0.2, gain: 0.16 },
  /** A cell scouring permanently. Lower and longer: this one does not come back. */
  scour: { type: 'sine', freq: [150, 84], dur: 0.42, gain: 0.22, noise: true },
  select: { type: 'triangle', freq: [520, 760], dur: 0.06, gain: 0.16 },
  start: { type: 'triangle', freq: [300, 640], dur: 0.2, gain: 0.24 },
  /** Six minutes up. */
  whistle: { type: 'sine', freq: [880, 420], dur: 0.7, gain: 0.26 },
};
